import * as React from "react";
import {
	Button,
	FormControlLabel,
	Paper,
	Switch,
	Tab,
	Tabs,
} from "@material-ui/core";
import { games } from "common/constants/Games";
import ActionsTab from "./ActionsTab";
import DetailsTab from "./DetailsTab";
import DiagnosticsTab from "./DiagnosticsTab";
import NotificationsTab from "./NotificationsTab";
import { useEventManagerStyles } from "./eventManagerStyles";
import { shortDate, shortUtc } from "./utcDate";

const gamePrefix = (gameId) => games.find((g) => g.id === gameId)?.prefix ?? "";

const isFuture = (value) => !!value && new Date(`${value}Z`) > new Date();

const phase = (event) => {
	if (isFuture(event.startDate)) {
		return "UPCOMING";
	}
	return isFuture(event.endDate) ? "LIVE" : "ENDED";
};

const lifecycle = (event) => (event.hidden === true ? "HIDDEN" : phase(event));

const statusLine = (event) => {
	const state = phase(event);
	if (state === "UPCOMING") {
		return `Starts ${shortUtc(event.startDate)} UTC`;
	}
	if (state === "ENDED") {
		const trophies =
			event.trophiesAwarded > 0 ? "trophies awarded" : "trophies not awarded";
		return `Ended ${shortDate(event.endDate)} · ${trophies}`;
	}
	const stageName = event.stages?.find(
		(s) => s.stageIndex === event.currentStage
	)?.name;
	return [
		stageName,
		event.picksOpen
			? `picks open until ${shortUtc(event.lockDate)} UTC`
			: "picks locked",
	]
		.filter(Boolean)
		.join(" · ");
};

export default function EventCard({
	event,
	isAdmin,
	onSave,
	onRewardTrophies,
	onNotifyStageOpen,
	onForceSync,
}) {
	const classes = useEventManagerStyles();
	const [active, setActive] = React.useState(null);
	const [hidden, setHidden] = React.useState(event.hidden === true);
	const [draft, setDraft] = React.useState({
		name: event.nameOverride ?? "",
		image: event.image ?? "",
		rewardTypeId: event.rewardTypeId ?? "",
	});

	React.useEffect(() => {
		setHidden(event.hidden === true);
	}, [event.hidden]);

	React.useEffect(() => {
		setDraft({
			name: event.nameOverride ?? "",
			image: event.image ?? "",
			rewardTypeId: event.rewardTypeId ?? "",
		});
	}, [event.nameOverride, event.image, event.rewardTypeId]);

	const displayName = event.nameOverride || event.name;
	const current = (event.stages ?? []).find(
		(s) => s.stageIndex === event.currentStage
	);
	const canSend =
		isAdmin &&
		event.picksOpen &&
		!!current &&
		current.picksOpenPush.status !== "sent";

	const toggleHidden = async (e) => {
		const next = e.target.checked;
		const send =
			!next &&
			canSend &&
			window.confirm(
				displayName +
					" goes public. Send the picks-open push now? Cancel skips it for this stage - you can still send it from the row later."
			);
		setHidden(next);
		if (!(await onSave({ hidden: next }))) {
			setHidden(!next);
			return;
		}
		if (send) {
			await onNotifyStageOpen();
		}
	};

	const sendPush = async () => {
		if (
			window.confirm(
				"Send the picks-open push for " +
					displayName +
					" (" +
					current.name +
					")?"
			)
		) {
			await onNotifyStageOpen();
		}
	};

	const life = lifecycle(event);
	const lifePill =
		life === "LIVE"
			? classes.pillLive
			: life === "UPCOMING"
			? classes.pillUpcoming
			: classes.pillDim;
	const image = draft.image || event.image;

	const tabs = [
		{
			id: "details",
			label: "Details",
			render: () => (
				<DetailsTab
					event={event}
					draft={draft}
					onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
					onSave={onSave}
				/>
			),
		},
		isAdmin && {
			id: "notifications",
			label: "Notifications",
			render: () => (
				<NotificationsTab
					event={event}
					canSend={canSend && !hidden}
					onSendPush={sendPush}
				/>
			),
		},
		{
			id: "actions",
			label: "Actions",
			render: () => (
				<ActionsTab
					event={event}
					isAdmin={isAdmin}
					onRewardTrophies={onRewardTrophies}
					onForceSync={onForceSync}
				/>
			),
		},
		isAdmin && {
			id: "diagnostics",
			label: "Diagnostics",
			render: () => <DiagnosticsTab eventId={event.id} />,
		},
	].filter(Boolean);

	return (
		<Paper className={classes.card} elevation={0}>
			<div className={classes.cardHead}>
				{image ? (
					<img className={classes.thumb} src={image} alt="" />
				) : (
					<div className={classes.thumb} />
				)}
				<div className={classes.cardText}>
					<div className={classes.titleRow}>
						<span className={classes.name}>{displayName}</span>
						<span className={classes.eventId}>#{event.id}</span>
						<span className={`${classes.pill} ${classes.pillCaps} ${lifePill}`}>
							{life}
						</span>
					</div>
					<div className={classes.status}>{statusLine(event)}</div>
				</div>
				<div className={classes.cardSide}>
					<Button
						variant="contained"
						className={classes.view}
						href={`/${gamePrefix(event.gameId)}/event/${event.id}`}
						target="_blank"
						rel="noopener noreferrer"
					>
						View
					</Button>
					<FormControlLabel
						className={classes.hiddenToggle}
						control={
							<Switch checked={hidden} onChange={toggleHidden} color="primary" />
						}
						label="Hidden"
						labelPlacement="start"
					/>
				</div>
			</div>
			<Tabs
				classes={{ root: classes.tabs }}
				value={active ?? false}
				onChange={(e, v) => setActive(v)}
				TabIndicatorProps={{ style: { display: "none" } }}
			>
				{tabs.map((t) => (
					<Tab
						key={t.id}
						classes={{ root: classes.tab }}
						value={t.id}
						label={t.label}
						disableRipple
						onClick={() => active === t.id && setActive(null)}
					/>
				))}
			</Tabs>
			{active ? (
				<div className={classes.panel}>
					{tabs.find((t) => t.id === active).render()}
				</div>
			) : null}
		</Paper>
	);
}
